/**
 * THE FLEET CARD ON A CUSTOMER'S PAGE.
 *
 * Anthony, 2026-10-14: a fleet dashboard for Shop Desk customers, first for
 * The JBI Group. The dashboard itself is public/fleet.html. Since
 * 2026-10-15 ("not just be an open link") the fleet manager and their
 * drivers sign in at /fleet-login.html with their cell and a texted code
 * (server/routes/fleet-auth.ts); this card is the switch that lets a
 * customer's number sign in at all, and "Open dashboard" lets the desk see
 * exactly what they see without their phone.
 *
 * Read and written with the signed-in person's own row security. A viewer
 * and a technician see the state and the link but not the switch, the same
 * line the customer fields above it draw. Columns from 0214: before it is
 * applied the read fails on an unknown column and the card says so rather
 * than showing a database error.
 */

import { db, api, customerSite } from "../data/client.ts";
import { ensurePortalToken } from "../data/jobs.ts";
import { esc } from "../ui/dom.ts";
import type { Role } from "../domain/permissions.ts";

type FleetRow = { fleet_account: boolean | null; fleet_name: string | null; portal_token: string | null };

const MAY_SWITCH: Role[] = ["admin", "supervisor", "advisor"];

export async function fleetCard(hostId: string, customerId: string, role: Role): Promise<void> {
  const host = document.getElementById(hostId);
  if (!host) return;
  const sb = await db();
  const { data, error } = await sb.from("customers")
    .select("fleet_account,fleet_name,portal_token").eq("id", customerId).maybeSingle();
  if (error || !data) {
    host.innerHTML = `<div class="card-head">Fleet account</div><div class="empty">${
      error && /column|42703/i.test(error.message)
        ? "Fleet accounts need migration 0214 on this database."
        : esc(error?.message ?? "Could not read this customer.")}</div>`;
    return;
  }
  const row = data as FleetRow;
  const may = MAY_SWITCH.includes(role);
  const link = row.fleet_account ? `${customerSite()}/fleet-login.html` : null;

  host.innerHTML = `<div class="card-head">Fleet account</div><div class="pad">
    ${row.fleet_account
      ? `<p class="quiet">On. They have a fleet dashboard: every vehicle, when each is due, a maintenance log, CSV upload and service requests that land on Leads.</p>`
      : `<p class="quiet">Off. Turn this on for a business with several vehicles. They get one link to a dashboard where they upload their vehicles from a CSV, keep a maintenance log and ask for service.</p>`}
    <label class="field"><span>Company name</span>
      <input id="fl-name" type="text" maxlength="120" value="${esc(row.fleet_name ?? "")}" placeholder="The JBI Group"${may ? "" : " disabled"}></label>
    ${link ? `<div class="kv"><span>They sign in at</span><b class="mono" style="overflow-wrap:anywhere">${esc(link)}</b></div>
      <p class="quiet">With the cell on this customer, and their drivers with their own cells. A code is texted each time. It is not a desk login.</p>
      <p><button class="btn sm" id="fl-open">Open dashboard</button>
        <button class="btn sm ghost" id="fl-copy">Copy sign-in link</button></p>` : ""}
    ${may ? `<p><button class="btn ${row.fleet_account ? "" : "primary "}sm" id="fl-save">${
      row.fleet_account ? "Save" : "Make this a fleet account"}</button>
      ${row.fleet_account ? `<button class="btn sm ghost" id="fl-off">Turn off</button>` : ""}</p>` : ""}
    <p class="msg" id="fl-msg" hidden></p>
  </div>`;

  const say = (t: string, good: boolean) => {
    const m = document.getElementById("fl-msg");
    if (m) { m.hidden = false; m.className = `msg ${good ? "good" : "bad"}`; m.textContent = t; }
  };

  const save = async (on: boolean) => {
    const name = (document.getElementById("fl-name") as HTMLInputElement | null)?.value.trim() ?? "";
    // The token first: a fleet account with no token is a switch with no door.
    if (on && !(await ensurePortalToken(customerId))) { say("Could not make their link. Try again.", false); return; }
    // Read back. An update that matches nothing answers with an empty list, not an error.
    const { data: saved, error: e } = await sb.from("customers")
      .update({ fleet_account: on, fleet_name: name || null }).eq("id", customerId).select("id");
    if (e || !(saved ?? []).length) { say(e?.message ?? "That did not save.", false); return; }
    await fleetCard(hostId, customerId, role);
    say(on ? "Saved. Text them the sign-in link." : "Turned off. They cannot sign in now.", true);
  };
  document.getElementById("fl-save")?.addEventListener("click", () => void save(true));
  document.getElementById("fl-off")?.addEventListener("click", () => {
    if (confirm("Turn off their fleet dashboard? They and their drivers cannot sign in. Nothing is deleted.")) void save(false);
  });
  /* OPEN DASHBOARD. The desk's own session buys a 12-hour fleet session for
     this one customer, handed over once to the login page on the customer
     site. The tab is opened first so a popup blocker does not eat it. */
  document.getElementById("fl-open")?.addEventListener("click", async () => {
    const w = window.open("about:blank", "_blank");
    try {
      const { data: { session } } = await sb.auth.getSession();
      const r = await fetch(api("/api/fleet/staff-open"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token ?? ""}` },
        body: JSON.stringify({ customer_id: customerId }),
      });
      const j = await r.json().catch(() => ({})) as { url?: string; error?: string };
      if (!r.ok || !j.url) throw new Error(j.error ?? "Could not open it.");
      if (w) w.location.href = `${customerSite()}${j.url}`; else location.href = `${customerSite()}${j.url}`;
    } catch (err) { w?.close(); say((err as Error).message, false); }
  });
  document.getElementById("fl-copy")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    try { await navigator.clipboard.writeText(link ?? ""); b.textContent = "Copied"; setTimeout(() => { b.textContent = "Copy sign-in link"; }, 1400); }
    catch { /* clipboard blocked; the link is on screen */ }
  });
}
